import { supabase } from '@/lib/supabase/client';

export interface Passenger {
  id: string;
  name: string;
  documentNumber?: string; // Unified identification
  dniNumber?: string;
  passportNumber?: string;
}

export interface TourReservationPayload {
  tourProductId: string;
  buyerUserId: string;
  quantityPeople: number;
  passengers: Passenger[];
  tourDate: string;
  pickupAddress: string;
  pricePerPerson: number;
  notes?: string;
}

export interface TourReservationResult {
  success: boolean;
  orderId?: string;
  total?: number;
  message: string;
}

const TOUR_STORE_ID = '30e79df7-81e8-4c5f-a825-602e5899d961';

export const useTourBooking = () => {
  const createTourReservation = async (
    userId: string,
    payload: TourReservationPayload
  ): Promise<TourReservationResult> => {
    try {
      const invalidPassenger = payload.passengers.find(
        p => !p.name || (!p.documentNumber && !p.dniNumber && !p.passportNumber)
      );
      if (invalidPassenger) {
        return {
          success: false,
          message: 'All passengers must have a name and either DNI or Passport',
        };
      }

      const totalPrice = payload.pricePerPerson * payload.quantityPeople;

      const { data: product } = await supabase
        .from('products')
        .select('*')
        .eq('id', payload.tourProductId)
        .single();

      if (!product) {
        return { success: false, message: 'Tour not found' };
      }

      const { data: order, error } = await supabase
        .from('orders')
        .insert({
          user_id: userId,
          store_id: TOUR_STORE_ID,
          status: 'pending',
          dispatch_status: 'not_published',
          items_list: {
            type: 'tour_reservation',
            productId: payload.tourProductId,
            tourTitle: product.title,
            quantityPeople: payload.quantityPeople,
            passengers: payload.passengers,
            tourDate: payload.tourDate,
            pickupAddress: payload.pickupAddress,
            pricePerPerson: payload.pricePerPerson,
            totalPrice,
            notes: payload.notes || null,
            createdAt: new Date().toISOString(),
          },
          total: totalPrice,
          currency: 'ARS',
          payment_method: 'transfer',
          notes: payload.notes || null,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating tour reservation:', error);
        return {
          success: false,
          message: 'Failed to create reservation',
        };
      }

      return {
        success: true,
        orderId: order.id,
        total: totalPrice,
        message: 'Reservation created successfully',
      };
    } catch (error) {
      console.error('Unexpected error:', error);
      return {
        success: false,
        message: 'Unexpected error occurred',
      };
    }
  };

  return {
    createTourReservation,
    loading: false,
  };
};
